import { useEffect, useMemo, useRef } from 'react';
import { CalculationType, ChartSubType, GridDashboardChartItem, HistoricalUtrs } from '@g17eco/types/insight-custom-dashboard';
import { groupTableUtrvs, groupedSeriesToChartData, toMonthStartKey } from '@features/custom-dashboard/items/groupTableUtrvs';
import { isNumericColumn } from '@features/custom-dashboard/items/tables/utils';
import { getMetricUnitDesc } from '@features/utr-modal/components/chart';
import {
  areBreakoutColumnsKnown,
  getBreakoutColumnCodes,
  isBreakoutCapableSubType,
} from '@features/custom-dashboard/utils/dashboard-utils';
import type { BarChartDataLoaded } from '@routes/custom-dashboard/items/charts/BarChart';
import { BarChart } from '@routes/custom-dashboard/items/charts/BarChart';
import { ColumnChart } from '@routes/custom-dashboard/items/charts/ColumnChart';
import { LineChart } from '@routes/custom-dashboard/items/charts/LineChart';
import { PieChart } from '@routes/custom-dashboard/items/charts/PieChart';
import type { ChartDataResponse } from '@routes/custom-dashboard/items/types';
import {
  getActualUtrvs,
  type CurrencyConfig,
} from '@routes/custom-dashboard/items/charts/multi-utrs-chart/utils';
import type { TableColumn } from '@g17eco/types/universalTracker';
import { handleRouteError, loggerMessage } from '../../../../../logger';
import type { ValueListPlain } from '@g17eco/types/valueList';
import { Note } from '../common/Note';

interface BreakoutChartWidgetProps {
  item: Pick<GridDashboardChartItem, 'subType' | 'variables' | 'breakout' | 'note'>;
  utrsData: HistoricalUtrs[];
  currencyConfig: CurrencyConfig | undefined;
  // Required: callers without a real value list pass `new Map()` rather than omitting,
  // so the resolveGroupLabel fallback path stays explicit at the call site.
  valueListMap: Map<string, ValueListPlain>;
}

// Breakout charts have no calculation; supply an empty Formula so the shared chart
// components render (their data comes from getChartData, which ignores calculation).
const BREAKOUT_EMPTY_CALCULATION = { type: CalculationType.Formula, values: [] };

type BreakoutValidation =
  | 'valid'
  | 'missing-group-column'
  | 'missing-value-column'
  | 'non-numeric-value';
type BreakoutErrorState = Exclude<BreakoutValidation, 'valid'>;

// Keyed by BreakoutErrorState so a new BreakoutValidation state fails to compile
// until both its user message and its Sentry log entry are filled in.
const BREAKOUT_VALIDATION_MESSAGES: Record<BreakoutErrorState, string> = {
  'missing-group-column':
    'The grouping column is no longer available on this metric. Edit the widget to reselect.',
  'missing-value-column':
    'The value column is incomplete or no longer available on this metric. Edit the widget to reselect.',
  'non-numeric-value':
    'The selected value column is no longer numeric. Edit the widget to choose a numeric column.',
};

const BREAKOUT_VALIDATION_LOG: Record<BreakoutErrorState, { message: string; context: string }> = {
  'missing-group-column': {
    message: 'BreakoutChartWidget: breakout group column not present on the metric',
    context: 'BreakoutChartWidget.missingGroupColumn',
  },
  'missing-value-column': {
    message: 'BreakoutChartWidget: breakout value column not present on the metric',
    context: 'BreakoutChartWidget.missingValueColumn',
  },
  'non-numeric-value': {
    message: 'BreakoutChartWidget: breakout value column is not numeric',
    context: 'BreakoutChartWidget.nonNumericValueColumn',
  },
};

// Order: group → value → numeric. Mixed errors surface the most upstream cause.
const resolveBreakoutValidation = ({
  breakoutColumnsKnown,
  valueColumnDef,
}: {
  breakoutColumnsKnown: boolean;
  valueColumnDef: TableColumn | undefined;
}): BreakoutValidation => {
  if (!breakoutColumnsKnown) {
    return 'missing-group-column';
  }
  if (valueColumnDef === undefined) {
    return 'missing-value-column';
  }
  if (!isNumericColumn(valueColumnDef.type)) {
    return 'non-numeric-value';
  }
  return 'valid';
};

// Several hooks below use primitive content keys (stableKey, breakoutColumnsKey,
// tableColumnsKey, dropSignature) as deps instead of the underlying `breakout` /
// `tableColumns` / `grouped` refs. Parent re-renders produce fresh references with
// no semantic change, so primitive deps prevent unnecessary memo recomputes and
// effect re-fires. Each oxlint-disable below names what its primitives capture.
export const BreakoutChartWidget = (props: BreakoutChartWidgetProps) => {
  const { item, utrsData, currencyConfig, valueListMap } = props;
  const { breakout, variables, subType, note } = item;

  // The metric variable's valueListCode is the single source of truth for the
  // measure column; breakout only stores the group-by column(s).
  const valueColumnCode = Object.values(variables)[0]?.valueListCode;

  const tableColumns: TableColumn[] = utrsData[0]?.utr.valueValidation?.table?.columns ?? [];
  const tableColumnsKey = tableColumns.map((c) => c.code).join(',');

  // Single-column-rendering assumption — grep getBreakoutColumnCodes when generalising.
  const [groupColumnCode] = getBreakoutColumnCodes(breakout);
  const resolveGroupLabel = useMemo((): ((rawValue: string) => string) => {
    if (groupColumnCode === undefined) {
      return (v) => v;
    }
    const groupColumn = tableColumns.find((c) => c.code === groupColumnCode);
    const listId = groupColumn?.listId;
    if (!listId) {
      return (v) => v;
    }
    const valueList = valueListMap.get(listId);
    if (!valueList) {
      return (v) => v;
    }
    return (rawValue: string) => {
      const option = valueList.options.find((opt) => opt.code === rawValue);
      return option?.name ?? rawValue;
    };
  // Captures: groupColumnCode + tableColumnsKey (in place of breakout / tableColumns refs).
  // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [groupColumnCode, tableColumnsKey, valueListMap]);

  const valueColumnDef =
    valueColumnCode !== undefined ? tableColumns.find((c) => c.code === valueColumnCode) : undefined;
  // Run the column unit through the same getMetricUnitDesc pipeline non-breakout charts
  // use, so the axis title is the expanded plural ("Kilowatt-hours") not the raw symbol.
  const sourceUtr = utrsData[0]?.utr;
  const valueColumnUnit =
    sourceUtr && valueColumnCode ? getMetricUnitDesc(sourceUtr, valueColumnCode, '') : '';
  const breakoutVAxisTitle = valueColumnUnit;
  const breakoutColumnsKnown = areBreakoutColumnsKnown(breakout, tableColumns.map((c) => c.code));

  const breakoutValidation = resolveBreakoutValidation({ breakoutColumnsKnown, valueColumnDef });

  // Primitive key for hooks below: group columns are reduced to a stable string so
  // ref-equal-but-structurally-identical re-renders don't bust memos.
  const breakoutColumnsKey = getBreakoutColumnCodes(breakout).join('|');
  const stableKey = `${valueColumnCode ?? ''}_${breakoutColumnsKey}`;

  // Run the transform once in the widget so the empty-state check and the chart share
  // a single groupTableUtrvs pass. Only Actual UTRVs feed the chart — match sibling
  // chart builders. Consuming every HistoricalUtrs entry stays defensive even though
  // GetWidget short-circuits the disaggregated case separately.
  const grouped = useMemo(
    () => {
      if (!breakout || !valueColumnCode || breakoutValidation !== 'valid') {
        return undefined;
      }
      const utrvs = getActualUtrvs(utrsData);
      return groupTableUtrvs(utrvs, breakout, valueColumnCode, { resolveGroupLabel });
    },
    // Captures: stableKey (= group-columns + valueColumnCode joined).
    // oxlint-disable-next-line react-hooks/exhaustive-deps
    [stableKey, resolveGroupLabel, utrsData, breakoutValidation]
  );

  const chartData = useMemo(
    () => (grouped ? groupedSeriesToChartData(grouped.series) : undefined),
    [grouped]
  );

  const getChartData = useMemo(
    () => (chartData ? async (): Promise<BarChartDataLoaded> => ({ chartData }) : null),
    [chartData]
  );

  // Pie is a snapshot: pick the most recent month-bucket across all groups, then build
  // one slice per surviving group with that bucket's value. Caption fields must come
  // from utrvs that actually contributed to the latest bucket — using getLatestActualUtrvs
  // on the raw utrsData would mismatch when groupTableUtrvs dropped the absolute-latest
  // utrv (missing group cell / non-numeric value column).
  const getPieChartData = useMemo(() => {
    if (!grouped) {
      return null;
    }
    const allPeriods = grouped.series.flatMap((s) => s.points.map((p) => p.period));
    if (allPeriods.length === 0) {
      return null;
    }
    const latestPeriod = allPeriods.sort().at(-1);
    const slices = grouped.series
      .map((s) => [s.label, s.points.find((p) => p.period === latestPeriod)?.value ?? 0] as [string, number])
      .filter(([, value]) => value !== 0);
    if (slices.length === 0) {
      return null;
    }
    const contributingUtrvs = getActualUtrvs(utrsData).filter(
      (utrv) => toMonthStartKey(utrv.effectiveDate) === latestPeriod
    );
    const effectiveDate = contributingUtrvs
      .map((utrv) => utrv.effectiveDate)
      .sort()
      .at(-1);
    const period = contributingUtrvs[0]?.period;
    const aggregated = contributingUtrvs.some((utrv) => utrv.aggregationCount);
    const chartData: ChartDataResponse['chartData'] = [['Key', 'Value'], ...slices];
    return async (): Promise<ChartDataResponse> => ({ chartData, effectiveDate, period, aggregated });
  }, [grouped, utrsData]);

  // Static parts of the note survive into the breakout view; the formula-driven `value`
  // can't evaluate without single-row scalar context, so strip those fields before
  // rendering so the note doesn't show as "prefix   postfix" with a blank middle.
  const breakoutNote = useMemo(() => {
    if (!note) {
      return undefined;
    }
    const { prefix, postfix } = note;
    if (!prefix && !postfix) {
      return undefined;
    }
    return { prefix, postfix };
  }, [note]);

  // Group-by code is part of dropSignature so swapping it re-fires the log even when
  // drop counts/samples are identical.
  const dropSignature = useMemo(() => {
    const d = grouped?.dropped;
    if (!d || (d.missingGroupCount === 0 && d.invalidValueCount === 0)) {
      return '';
    }
    return `${valueColumnCode ?? ''}|${breakoutColumnsKey}|${d.missingGroupCount}|${d.invalidValueCount}|${d.droppedSamples.join('|')}`;
  }, [grouped, valueColumnCode, breakoutColumnsKey]);

  useEffect(() => {
    if (!dropSignature || !grouped) {
      return;
    }
    loggerMessage('Breakout chart: dropped table rows during grouping', {
      level: 'error',
      valueColumnCode,
      missingGroupCount: grouped.dropped.missingGroupCount,
      invalidValueCount: grouped.dropped.invalidValueCount,
      droppedSamples: grouped.dropped.droppedSamples,
    });
    // Captures: dropSignature (= valueColumnCode + breakoutColumnsKey + drop content).
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [dropSignature]);

  // Dedupe so editing the metric/value codes mid-session doesn't fire a Sentry log
  // per keystroke. Track the last logged validation state per widget instance and
  // only re-fire when it changes (returning to 'valid' resets so a later failure logs).
  const loggedValidationRef = useRef<BreakoutErrorState | undefined>(undefined);
  useEffect(() => {
    if (!breakout || breakoutValidation === 'valid') {
      loggedValidationRef.current = undefined;
      return;
    }
    if (loggedValidationRef.current === breakoutValidation) {
      return;
    }
    loggedValidationRef.current = breakoutValidation;
    const log = BREAKOUT_VALIDATION_LOG[breakoutValidation];
    handleRouteError(new Error(log.message), {
      context: log.context,
      breakoutColumns: breakout.columns.map((c) => c.code).join(','),
      valueColumnCode: valueColumnCode ?? '',
      valueColumnType: valueColumnDef?.type ?? '',
      availableColumns: tableColumns.map((c) => c.code).join(','),
    });
    // Captures: validation state + breakoutColumnsKey + valueColumnCode + tableColumnsKey.
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [breakoutValidation, breakoutColumnsKey, valueColumnCode, tableColumnsKey]);

  // Use a primitive dep so a parent re-render that hands down a fresh item object
  // (and therefore a fresh `breakout` reference) doesn't re-fire handleRouteError.
  const hasBreakout = breakout !== undefined;
  useEffect(() => {
    if (!hasBreakout) {
      return;
    }
    if (isBreakoutCapableSubType(subType)) {
      return;
    }
    handleRouteError(
      new Error(`BreakoutChartWidget: unexpected subType ${subType}`),
      { context: 'BreakoutChartWidget.unexpectedSubType', subType }
    );
  }, [subType, hasBreakout]);

  if (!breakout) {
    return null;
  }

  if (breakoutValidation !== 'valid') {
    return <div className='p-3 text-ThemeTextLight'>{BREAKOUT_VALIDATION_MESSAGES[breakoutValidation]}</div>;
  }

  if (!isBreakoutCapableSubType(subType)) {
    return (
      <div className='p-3 text-ThemeTextLight'>
        This chart type doesn&apos;t support grouping. Edit the widget and choose Bar, Column, Line, or Pie.
      </div>
    );
  }

  const isPieSubType =
    subType === ChartSubType.Pie || subType === ChartSubType.FullPie || subType === ChartSubType.PieWithLegend;

  // groupedSeriesToChartData always returns a header row; a second row means there
  // is at least one period of plottable data. Without it every row was dropped.
  const hasChartRows = chartData !== undefined && chartData.length > 1;
  // Each subtype has its own data-readiness gate: pie collapses to a latest-period
  // snapshot (its own getPieChartData null check), bar/column/line need at least
  // one plottable row out of groupedSeriesToChartData.
  const hasPlottableData = isPieSubType ? getPieChartData !== null : getChartData !== null && hasChartRows;

  // Static note is shared across all chart branches and the empty-state. Bar/Column/
  // Line don't render <Note> themselves; passing variablesWithValues is moot because
  // breakoutNote has no `value` field after the strip.
  const noteElement = breakoutNote ? (
    <Note note={breakoutNote} variablesWithValues={{}} />
  ) : null;

  if (!hasPlottableData) {
    // Distinguish "every row excluded" from "genuinely empty dataset" — both look
    // like a header-only chartData, but the cause and fix are different.
    const everyRowDropped =
      grouped !== undefined &&
      (grouped.dropped.missingGroupCount > 0 || grouped.dropped.invalidValueCount > 0);
    return (
      <div className='p-3 text-ThemeTextLight'>
        {everyRowDropped
          ? 'All rows were excluded because of missing group values or non-numeric measure values. Check the underlying metric data.'
          : 'No data available for the selected grouping.'}
      </div>
    );
  }

  if (isPieSubType && getPieChartData) {
    return (
      <>
        {noteElement}
        <PieChart
          getChartData={getPieChartData}
          utrsData={utrsData}
          variables={variables}
          calculation={BREAKOUT_EMPTY_CALCULATION}
          subType={subType}
        />
      </>
    );
  }

  // Pie was handled above; bar/column/line all share the bar-shaped getChartData.
  if (!getChartData) {
    return null;
  }

  if (subType === ChartSubType.Line) {
    return (
      <>
        {noteElement}
        <LineChart
          getChartData={getChartData}
          utrsData={utrsData}
          variables={variables}
          calculation={BREAKOUT_EMPTY_CALCULATION}
          currencyConfig={currencyConfig}
          unitText={valueColumnUnit}
          vAxisTitle={breakoutVAxisTitle}
        />
      </>
    );
  }

  if (subType === ChartSubType.Column) {
    return (
      <>
        {noteElement}
        <ColumnChart
          getChartData={getChartData}
          utrsData={utrsData}
          variables={variables}
          calculation={BREAKOUT_EMPTY_CALCULATION}
          currencyConfig={currencyConfig}
          unitText={valueColumnUnit}
          vAxisTitle={breakoutVAxisTitle}
          isStacked={true}
        />
      </>
    );
  }

  return (
    <>
      {noteElement}
      <BarChart
        getChartData={getChartData}
        utrsData={utrsData}
        variables={variables}
        calculation={BREAKOUT_EMPTY_CALCULATION}
        currencyConfig={currencyConfig}
        unitText={valueColumnUnit}
        vAxisTitle={breakoutVAxisTitle}
        isStacked={true}
        showValueAxisTitle={true}
        hideTakenFromText={true}
      />
    </>
  );
};
